import React from 'react';
import PropTypes from 'prop-types';

import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

const ButtonProgress = styled(CircularProgress)(({ theme }) => ({
  color: theme.palette.secondary.main,
  margin: 'auto',
  pointerEvents: 'none',
}));

const Icon = styled(Box, {
  shouldForwardProp: prop => !['disabled', 'loading'].includes(prop),
})(({ disabled, loading, theme }) => ({
  '& > svg > path': {
    fill: !disabled && !loading ? theme.palette.secondary.main : theme.palette.graphic.main,
  },
  alignItems: 'center',
  backgroundColor:
    !disabled && !loading ? theme.palette.secondary.medium : theme.palette.graphic.light,
  borderRadius: '4px 4px 0 0',
  display: 'flex',
  fontSize: '58px',
  height: 109,
  justifyContent: 'center',
  width: '100%',
}));

const Root = styled(ButtonBase)(({ theme }) => ({
  '&:hover': {
    boxShadow: theme.shadows[6],
  },
  borderRadius: 4,
  boxShadow: theme.shadows[1],
  color: theme.palette.secondary.main,
  flexDirection: 'column',
  height: 156,
  margin: theme.spacing(1.5),
  width: 156,
}));

const Title = styled(Typography, {
  shouldForwardProp: prop => !['disabled'].includes(prop),
})(({ disabled, theme }) => ({
  ...theme.typography.caption,
  color: disabled ? theme.palette.text.disabled : theme.palette.common.black,
  fontWeight: 400,
  maxWidth: 98,
  textAlign: 'center',
}));

const TitleContainer = styled(Box)(() => ({
  alignItems: 'center',
  borderRadius: '0 0 4px 4px',
  display: 'flex',
  height: 46,
  justifyContent: 'center',
  width: '100%',
}));

const SmallModuleButton = props => {
  // in future add flags as prop
  const { comingSoon, disabled, icon, label, loading, to } = props;
  const rootProps = disabled
    ? {
        'aria-disabled': 'true',
        sx: { opacity: 0.5, pointerEvents: 'none' },
        tabIndex: -1,
      }
    : {
        component: RouterLink,
        to,
      };

  return (
    <Stack spacing={1} useFlexGap>
      <Root {...rootProps}>
        <Icon disabled={disabled} loading={loading}>
          {loading ? <ButtonProgress size={44} /> : icon}
        </Icon>
        <TitleContainer>
          <Title disabled={disabled}>{label}</Title>
        </TitleContainer>
      </Root>
      {comingSoon && (
        <Box
          sx={{
            fontSize: 14,
            mx: 0,
            my: 0.5,
            textAlign: 'center',
          }}
        >
          (coming soon)
        </Box>
      )}
    </Stack>
  );
};

SmallModuleButton.propTypes = {
  comingSoon: PropTypes.bool,
  disabled: PropTypes.bool.isRequired,
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  to: PropTypes.string,
};

SmallModuleButton.defaultProps = {
  comingSoon: false,
  to: undefined,
};

export default SmallModuleButton;
